import { Button, TextInput } from '@consigli/facade';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlinePlus } from 'react-icons/hi';

import { BidCard } from './bid-card';
import { ChooseGapsPackage } from './choose-gaps-package';
import { LegalWarning } from './legal-warning';
import { OtherBidsSelector } from './other-bids-selector';
import { Bid, CompareConfig, MAX_COMPARISON_BIDS, OtherBidReference } from './types';

interface BidDetailsStepProps {
  packageName: string;
  onPackageNameChange: (packageName: string) => void;
  nameErrorKey: string | null;
  bids: Bid[];
  onBidsChange: (bids: Bid[]) => void;
  compareConfig: CompareConfig;
  onCompareConfigChange: (config: CompareConfig) => void;
  selectedOtherBids: OtherBidReference[];
  onSelectedOtherBidsChange: (selectedOtherBids: OtherBidReference[]) => void;
  onAddBid: () => void;
  onBack: () => void;
  onNext: () => void;
  isNextEnabled: boolean;
}

export const BidDetailsStep: FC<BidDetailsStepProps> = ({
  packageName,
  onPackageNameChange,
  nameErrorKey,
  bids,
  onBidsChange,
  compareConfig,
  onCompareConfigChange,
  selectedOtherBids,
  onSelectedOtherBidsChange,
  onAddBid,
  onBack,
  onNext,
  isNextEnabled,
}) => {
  const { t } = useTranslation();

  const updateBid = (updatedBid: Bid) => {
    onBidsChange(bids.map((bid) => (bid.id === updatedBid.id ? updatedBid : bid)));
  };

  const removeBid = (bidId: string) => {
    onBidsChange(bids.filter((bid) => bid.id !== bidId));
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <LegalWarning />

      <div className="w-full rounded-lg border border-day-light-2 bg-day-light-5 px-6 py-5">
        <label className="mb-1 block text-sm font-medium text-day-neutral-dark">
          {t('bid-review.bid-details.package-name')}
        </label>
        <TextInput
          value={packageName}
          onChange={(event) => onPackageNameChange(event.target.value)}
          placeholder={t('bid-review.bid-details.package-name-placeholder')}
        />
        {nameErrorKey && <div className="mt-1 text-sm text-red-700">{t(nameErrorKey)}</div>}
      </div>

      <div className="w-full rounded-lg border border-day-light-2 bg-day-light-5 p-6">
        <ChooseGapsPackage config={compareConfig} onChange={onCompareConfigChange} />
      </div>

      <div className="w-full rounded-lg border border-day-light-2 bg-day-light-5 p-6">
        <p className="text-lg font-semibold text-day-neutral-dark">
          {t('bid-review.bid-details.add-bids-title')}
        </p>
        <p className="text-sm text-day-neutral-subtle">
          {t('bid-review.bid-details.add-bids-description')}
        </p>

        <div className="mt-5 border-t border-day-light-2 pt-5">
          <OtherBidsSelector
            bids={bids}
            selectedOtherBids={selectedOtherBids}
            onSelectedOtherBidsChange={onSelectedOtherBidsChange}
          />
        </div>

        {selectedOtherBids.length < MAX_COMPARISON_BIDS && (
          <div className="mt-5 flex flex-col gap-4 border-t border-day-light-2 pt-5">
            <p className="font-semibold text-day-neutral-dark">
              {t('bid-review.bid-details.upload-new-bids')}
            </p>
            {bids.map((bid, index) => (
              <BidCard
                key={bid.id}
                bid={bid}
                index={index}
                canRemove={bids.length > 1}
                onChange={updateBid}
                onRemove={() => removeBid(bid.id)}
              />
            ))}

            {bids.length + selectedOtherBids.length < MAX_COMPARISON_BIDS && (
              <Button
                secondary
                icon={HiOutlinePlus}
                iconSize={18}
                rounded
                onClick={onAddBid}
                className="w-full border-dashed border-day-light-1 bg-day-light-5 py-3 text-sm hover:bg-day-light-4"
              >
                {t('bid-review.bid-details.add-bid')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-4">
        <Button secondary rounded onClick={onBack} className="px-8 text-sm">
          {t('package-creation.back')}
        </Button>
        <Button primary rounded onClick={onNext} disabled={!isNextEnabled} className="px-8 text-sm">
          {t('package-creation.next')}
        </Button>
      </div>
    </div>
  );
};
